import { trackError, ErrorSeverity } from "@/utils/error-tracking";

const HEIC_EXTENSION = /\.hei[cf]$/i;
const DEFAULT_JPEG_QUALITY = 0.8;

/**
 * Whether a photo needs decoding before a browser can draw it.
 *
 * The type alone missed every HEIC from Chrome on Windows: the OS registers no
 * MIME for .heic, so the browser reports an empty type and the photo went
 * straight to an <img> that could not read it (the rows from 2026-09-09/10 say
 * fileType unknown, fileExtension heic). The extension is consulted only when
 * the browser gave no real type, so a file it has positively typed is believed.
 */
export const isHeicFile = (file: File): boolean => {
	// File.type and File.name are always strings; an unknown type is "".
	const type = file.type;
	if (type.startsWith("image/heic") || type.startsWith("image/heif")) return true;
	const untyped = !type || type === "application/octet-stream";
	return untyped && HEIC_EXTENSION.test(file.name);
};

/** heic-to rejects with a string; a worker crash hands back an event or nothing. */
const describeFailure = (e: unknown): string => {
	if (typeof e === "string") return e;
	if (e instanceof Error) return e.message;
	const message = (e as { message?: unknown } | null | undefined)?.message;
	if (typeof message === "string") return message;
	return e === undefined ? "no reason given" : String(e);
};

/**
 * Decode an iPhone HEIC into a JPEG the canvas pipeline can draw.
 *
 * heic-to follows libheif releases. heic2any, which this replaced, bundled a
 * 2021 libheif that failed newer iPhone photos with "Could not parse HEIF file",
 * rejecting with a plain object that reached the error log as "[object Object]".
 *
 * Never rejects and never returns nothing. If the decoder fails, the original
 * file comes back so the browser can try its own decoder: Safari reads HEIC
 * natively, so a Safari seller still gets the photo. Anywhere else the <img>
 * fails too, and the caller logs that.
 */
export const convertHeicToJpeg = async (
	file: File,
	quality: number = DEFAULT_JPEG_QUALITY,
): Promise<Blob> => {
	try {
		const { heicTo } = await import("heic-to");
		return await heicTo({ blob: file, type: "image/jpeg", quality });
	} catch (e) {
		const name = file.name;
		const dot = name.lastIndexOf(".");
		trackError(
			new Error(`HEIC decode failed: ${describeFailure(e).slice(0, 200)}`),
			ErrorSeverity.ERROR,
			{
				action: "heic-decode",
				component: "heic",
				extra: {
					decoder: "heic-to",
					fileType: file.type || "unknown",
					fileSize: file.size,
					fileExtension: dot > -1 ? name.slice(dot + 1).toLowerCase() : "none",
				},
			},
		);
		return file;
	}
};
